import { useT } from "@magic-md/i18n/react";
import { PanelBottomClose, PanelBottomOpen } from "lucide-react";
import {
    type ComponentPropsWithoutRef,
    type ReactNode,
    memo,
    useEffect,
    useRef,
    useState,
} from "react";
import { cn } from "../../../lib/utils";

type RenderUnitFooterDisclosureProps = Omit<
    ComponentPropsWithoutRef<"div">,
    "children" | "className" | "title"
> & {
    children: ReactNode;
    autoOpen?: boolean;
    footerClassName?: string;
    footerTitle?: string;
    isFullscreen?: boolean;
    toggleLabel: string;
};

export const RenderUnitFooterDisclosure = memo(function RenderUnitFooterDisclosure({
    autoOpen = false,
    children,
    footerClassName,
    footerTitle,
    isFullscreen = false,
    toggleLabel,
    ...footerProps
}: RenderUnitFooterDisclosureProps) {
    const t = useT("editor");
    const [userOpen, setUserOpen] = useState(false);
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const open = autoOpen || userOpen;
    const accessibleLabel = open
        ? `${t("common.collapse")} ${toggleLabel}`
        : t("render.expandUnit", { id: toggleLabel });

    useEffect(() => {
        if (isFullscreen) return undefined;
        const toggle = toggleRef.current;
        const host = toggle?.closest<HTMLElement>(".magic-render-unit");
        if (!toggle || !host) return undefined;

        host.classList.add("magic-render-unit-has-footer-toggle");

        const updateTogglePosition = (event?: PointerEvent) => {
            const rect = host.getBoundingClientRect();
            const buttonHeight = toggle.offsetHeight || 20;
            const inset = 4;
            const relativeX = event ? event.clientX - rect.left : Number.POSITIVE_INFINITY;
            const leftRailActive = relativeX >= -24 && relativeX <= 32;
            const rightRailActive = relativeX >= rect.width - 32 && relativeX <= rect.width + 24;
            const railActive = leftRailActive || rightRailActive;
            const railSide = rightRailActive ? "right" : "left";
            let leftClippedByAncestor = false;
            let rightClippedByAncestor = false;
            let ancestor = host.parentElement;
            while (ancestor && ancestor !== document.body) {
                const style = window.getComputedStyle(ancestor);
                if (/(auto|clip|hidden|scroll)/.test(style.overflowX)) {
                    const ancestorRect = ancestor.getBoundingClientRect();
                    if (rect.left - 24 < ancestorRect.left + 2) {
                        leftClippedByAncestor = true;
                    }
                    if (rect.right + 24 > ancestorRect.right - 2) {
                        rightClippedByAncestor = true;
                    }
                }
                ancestor = ancestor.parentElement;
            }
            const useInsideRail = railSide === "left"
                ? rect.left < 34 || leftClippedByAncestor
                : window.innerWidth - rect.right < 34 || rightClippedByAncestor;
            let maxTop = Math.max(inset, rect.height - buttonHeight - inset);
            if (railSide === "right" && useInsideRail) {
                const openFooter = host.querySelector<HTMLElement>(
                    "[data-render-footer-disclosure='true'][data-render-footer-open='true'] .magic-render-footer",
                );
                const footerRect = openFooter?.getBoundingClientRect();
                if (
                    footerRect &&
                    footerRect.height > 0 &&
                    footerRect.top > rect.top &&
                    footerRect.top < rect.bottom
                ) {
                    maxTop = Math.min(
                        maxTop,
                        Math.max(inset, footerRect.top - rect.top - buttonHeight - inset),
                    );
                }
            }
            const rawTop = event
                ? event.clientY - rect.top - buttonHeight / 2
                : inset;
            const nextTop = Math.round(Math.min(maxTop, Math.max(inset, rawTop)));
            host.style.setProperty("--magic-render-footer-toggle-y", `${nextTop}px`);
            host.classList.toggle("magic-render-unit-footer-toggle-active", railActive);
            host.classList.toggle("magic-render-unit-footer-toggle-inside", useInsideRail);
            host.classList.toggle("magic-render-unit-footer-toggle-right", railSide === "right");
            host.classList.toggle("magic-render-unit-footer-toggle-left", railSide === "left");
            host.dataset.renderFooterToggleRail = railActive ? "active" : "inactive";
            host.dataset.renderFooterToggleSide = railActive ? railSide : "none";
        };

        const hideToggle = () => {
            host.classList.remove("magic-render-unit-footer-toggle-active");
            host.dataset.renderFooterToggleRail = "inactive";
            host.dataset.renderFooterToggleSide = "none";
        };

        updateTogglePosition();
        host.addEventListener("pointerenter", updateTogglePosition);
        host.addEventListener("pointermove", updateTogglePosition);
        host.addEventListener("pointerleave", hideToggle);

        return () => {
            host.removeEventListener("pointerenter", updateTogglePosition);
            host.removeEventListener("pointermove", updateTogglePosition);
            host.removeEventListener("pointerleave", hideToggle);
            host.classList.remove("magic-render-unit-has-footer-toggle");
            host.classList.remove("magic-render-unit-footer-toggle-active");
            host.classList.remove("magic-render-unit-footer-toggle-inside");
            host.classList.remove("magic-render-unit-footer-toggle-left");
            host.classList.remove("magic-render-unit-footer-toggle-right");
            host.style.removeProperty("--magic-render-footer-toggle-y");
            delete host.dataset.renderFooterToggleRail;
            delete host.dataset.renderFooterToggleSide;
        };
    }, [isFullscreen]);

    if (isFullscreen) {
        return (
            <div
                className={cn(footerClassName, "magic-render-footer-open")}
                title={footerTitle}
                {...footerProps}
            >
                {children}
            </div>
        );
    }

    return (
        <>
            <button
                ref={toggleRef}
                type="button"
                className={cn(
                    "magic-render-footer-toggle",
                    open && "magic-render-footer-toggle-open",
                )}
                aria-expanded={open}
                aria-label={accessibleLabel}
                title={accessibleLabel}
                data-render-footer-toggle="true"
                data-render-footer-open={open ? "true" : "false"}
                onClick={(event) => {
                    event.stopPropagation();
                    setUserOpen((current) => !current);
                }}
            >
                {open ? (
                    <PanelBottomClose size={11} aria-hidden="true" />
                ) : (
                    <PanelBottomOpen size={11} aria-hidden="true" />
                )}
            </button>
            <div
                className={cn(
                    "magic-render-footer-disclosure",
                    open && "magic-render-footer-disclosure-open",
                )}
                data-render-footer-disclosure="true"
                data-render-footer-open={open ? "true" : "false"}
            >
                <div className="magic-render-footer-disclosure-inner">
                    <div
                        className={cn(
                            footerClassName,
                            open
                                ? "magic-render-footer-open"
                                : "magic-render-footer-collapsed",
                        )}
                        title={footerTitle}
                        {...footerProps}
                    >
                        {children}
                    </div>
                </div>
            </div>
        </>
    );
});
